import { useState, useEffect } from 'react';
import { CotisationSalariale } from '../types';
import { getCotisationsSalariales, validateCotisationData, DEFAULT_COTISATIONS } from '../services/cotisationService';

/**
 * @fileoverview Hook for managing cotisations_salariales data from Supabase
 * 
 * This hook provides a clean interface for components to access cotisation data
 * with proper loading states, error handling, and fallback to hardcoded data.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

interface UseCotisationsDataResult {
  cotisations: CotisationSalariale[];
  isLoadingCotisations: boolean;
  setIsLoadingCotisations: (loading: boolean) => void;
  errorCotisations: string | null;
  isUsingFallbackCotisations: boolean;
  refetchCotisations: () => Promise<void>;
}

/**
 * Custom hook for fetching and managing cotisations_salariales data
 * 
 * This hook handles the complete lifecycle of cotisation data:
 * - Fetches data from Supabase on mount
 * - Provides loading and error states
 * - Falls back to hardcoded data if Supabase fails
 * - Validates data integrity
 * - Provides a refetch function for manual refresh
 * 
 * @returns {UseCotisationsDataResult} Object containing cotisations data and state
 * 
 * @example
 * function MyComponent() {
 *   const { cotisations, isLoadingCotisations, errorCotisations, isUsingFallbackCotisations } = useCotisationsData();
 *   
 *   if (isLoadingCotisations) return <LoadingSpinner />;
 *   if (errorCotisations) return <ErrorMessage error={errorCotisations} />;
 *   
 *   return <CotisationsList cotisations={cotisations} />;
 * }
 */
export function useCotisationsData(): UseCotisationsDataResult {
  const [cotisations, setCotisations] = useState<CotisationSalariale[]>([]);
  const [isLoadingCotisations, setIsLoadingCotisations] = useState(true);
  const [errorCotisations, setErrorCotisations] = useState<string | null>(null);
  const [isUsingFallbackCotisations, setIsUsingFallbackCotisations] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoadingCotisations(true);
      setErrorCotisations(null);
      setIsUsingFallbackCotisations(false);

      console.log('Fetching cotisations_salariales data from Supabase...');
      const supabaseCotisations = await getCotisationsSalariales();
      
      // Validate the fetched data
      if (!validateCotisationData(supabaseCotisations)) {
        console.warn('Invalid cotisation data structure received from Supabase. Falling back to hardcoded data.');
        setCotisations(DEFAULT_COTISATIONS);
        setIsUsingFallbackCotisations(true);
        setErrorCotisations('Invalid cotisation data structure received from Supabase. Using fallback data.');
        setIsLoadingCotisations(false);
        return;
      }

      setCotisations(supabaseCotisations);
      console.log('Successfully loaded cotisation data from Supabase');
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Failed to fetch cotisation data from Supabase:', errorMessage);
      
      // Fallback to hardcoded data
      console.log('Falling back to hardcoded cotisation data');
      setCotisations(DEFAULT_COTISATIONS);
      setIsUsingFallbackCotisations(true);
      setErrorCotisations(`Supabase connection failed: ${errorMessage}. Using fallback data.`);
      
    } finally {
      setIsLoadingCotisations(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const refetchCotisations = async () => {
    await fetchData();
  };

  return {
    cotisations,
    isLoadingCotisations,
    setIsLoadingCotisations,
    errorCotisations,
    isUsingFallbackCotisations,
    refetchCotisations
  };
}